import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import swal from 'sweetalert';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DomainService from '../../../services/DomainService';
import { selectIsAdmin } from '../../../store/selectors/AuthSelectors';
import { PageHeader, Card, Button, Badge, Field, Input, EmptyState } from '../../ui';
import defaultLogo from '../../../images/teve-logo-wide.png';

const statusTone = (s) => ({
  verified: 'good', active: 'good', pending: 'warn', failed: 'live',
}[s] || 'neutral');

const sslLabel = (s) => ({
  active: 'SSL active', provisioning: 'Issuing SSL…', pending: 'SSL pending', error: 'SSL failed',
}[s] || 'SSL pending');
const sslTone = (s) => ({ active: 'good', provisioning: 'warn', error: 'live' }[s] || 'neutral');

const CustomDomainManager = () => {
  const isAdmin = useSelector(selectIsAdmin);
  const [access, setAccess] = useState({ enabled: null, reason: null });
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [instructionsFor, setInstructionsFor] = useState(null); // {domain, instructions}
  const [verifyingId, setVerifyingId] = useState(null);

  // branding
  const [brand, setBrand] = useState({ brand_name: '', logo_url: null });
  const [savingBrand, setSavingBrand] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const st = await DomainService.getStatus();
      setAccess({ enabled: !!st.data?.enabled, reason: st.data?.reason });
      if (st.data?.enabled) {
        const [d, b] = await Promise.all([DomainService.getDomains(), DomainService.getBranding().catch(() => null)]);
        setDomains(d.data || []);
        if (b?.branding) setBrand(b.branding);
      }
    } catch (e) {
      setAccess({ enabled: false, reason: e.code });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addDomain = async (e) => {
    e.preventDefault();
    const domain = newDomain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    if (!domain) return;
    setAdding(true);
    try {
      await DomainService.createDomain({ domain });
      toast.success('Domain added — now verify ownership');
      setNewDomain('');
      load();
    } catch (err) {
      toast.error(err.message || 'Failed to add domain');
    } finally {
      setAdding(false);
    }
  };

  const showInstructions = async (d) => {
    try {
      const res = await DomainService.getVerificationInstructions(d.id);
      setInstructionsFor({ domain: d, data: res.data });
    } catch {
      toast.error('Failed to load instructions');
    }
  };

  const verify = async (d) => {
    setVerifyingId(d.id);
    try {
      const res = await DomainService.verifyDomain(d.id);
      toast.success(res?.message || `${d.domain} verified — issuing SSL certificate…`);
      setInstructionsFor(null);
      load();
    } catch (err) {
      toast.error(err.message || 'Verification failed — check your DNS records and try again');
    } finally {
      setVerifyingId(null);
    }
  };

  const retrySSL = async (d) => {
    setVerifyingId(d.id);
    try {
      await DomainService.provisionSSL(d.id);
      toast.success(`SSL certificate issued for ${d.domain}`);
      load();
    } catch (err) {
      toast.error(err.message || 'Could not issue the certificate yet — check DNS and retry in a minute');
    } finally {
      setVerifyingId(null);
    }
  };

  const removeDomain = (d) => {
    swal({
      title: `Remove ${d.domain}?`,
      text: 'Your portal will no longer be reachable at this address.',
      icon: 'warning', buttons: ['Cancel', 'Remove'], dangerMode: true,
    }).then(async (ok) => {
      if (!ok) return;
      try { await DomainService.deleteDomain(d.id); toast.success('Domain removed'); load(); }
      catch { toast.error('Failed to remove domain'); }
    });
  };

  const saveBrandName = async () => {
    setSavingBrand(true);
    try { await DomainService.setBrandName(brand.brand_name); toast.success('Brand name saved'); }
    catch { toast.error('Failed to save'); }
    finally { setSavingBrand(false); }
  };

  const onLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      await DomainService.uploadLogo(file);
      toast.success('Logo updated');
      const b = await DomainService.getBranding();
      setBrand(b.branding);
    } catch (err) {
      toast.error(err.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeLogo = async () => {
    try { await DomainService.removeLogo(); setBrand((b) => ({ ...b, logo_url: null })); toast.success('Logo removed'); }
    catch { toast.error('Failed to remove logo'); }
  };

  if (loading) {
    return <div className="tw-py-20 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-2xl tw-text-brand" /></div>;
  }

  if (!access.enabled && !isAdmin) {
    return (
      <>
        <PageHeader title="White-label Portal" subtitle="Serve Teve under your own domain and logo" />
        <Card>
          <EmptyState
            icon="fa-crown"
            title="Available on higher plans"
            hint="Custom domains and white-label branding are included with the Professional and Enterprise plans. Upgrade to make the portal your own."
            action={<Button icon="fa-arrow-up" onClick={() => { window.location.href = '/pricing'; }}>See plans</Button>}
          />
        </Card>
      </>
    );
  }

  const previewLogo = brand.logo_url || defaultLogo;

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <PageHeader
        title="White-label Portal"
        subtitle="Your customers see the portal on your domain, with your logo"
      />

      {/* Branding */}
      <div className="tw-mb-6 tw-grid tw-gap-6 lg:tw-grid-cols-3">
        <div className="lg:tw-col-span-2">
          <Card title="Branding">
            <div className="tw-space-y-5">
              <div>
                <div className="tw-mb-1.5 tw-text-sm tw-font-semibold tw-text-heading">Portal logo</div>
                <p className="tw-mb-3 tw-text-xs tw-text-content-faint">PNG, JPG, WEBP or SVG · max 2 MB · shown in the sidebar and on the login screen.</p>
                <div className="tw-flex tw-items-center tw-gap-4">
                  <div className="tw-flex tw-h-14 tw-w-40 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-line tw-bg-sunken tw-p-2">
                    <img src={previewLogo} alt="logo preview" className="tw-max-h-full tw-max-w-full" onError={(e) => { e.target.src = defaultLogo; }} />
                  </div>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={onLogoPick} hidden />
                  <Button variant="secondary" icon="fa-upload" onClick={() => fileRef.current?.click()} disabled={uploadingLogo}>
                    {uploadingLogo ? 'Uploading…' : brand.logo_url ? 'Replace' : 'Upload'}
                  </Button>
                  {brand.logo_url && <Button variant="ghost" icon="fa-trash" onClick={removeLogo}>Remove</Button>}
                </div>
              </div>

              <Field label="Brand name" hint="Used as the browser tab title on your domain.">
                <div className="tw-flex tw-gap-2">
                  <Input value={brand.brand_name || ''} onChange={(e) => setBrand((b) => ({ ...b, brand_name: e.target.value }))} placeholder="e.g. Acme Live" maxLength={60} />
                  <Button onClick={saveBrandName} disabled={savingBrand}>{savingBrand ? 'Saving…' : 'Save'}</Button>
                </div>
              </Field>
            </div>
          </Card>
        </div>

        <Card title="Preview">
          <div className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-line">
            <div className="tw-flex tw-items-center tw-gap-2 tw-px-3" style={{ height: 46, background: 'var(--c-sidebar)' }}>
              <img src={previewLogo} alt="" className="tw-max-h-6" onError={(e) => { e.target.src = defaultLogo; }} />
            </div>
            <div className="tw-space-y-2 tw-bg-surface tw-p-3">
              {['Dashboard', 'Live Streams', 'Analytics'].map((x) => (
                <div key={x} className="tw-text-xs tw-text-content-muted">{x}</div>
              ))}
            </div>
            <div className="tw-border-t tw-border-line tw-bg-surface tw-px-3 tw-py-2 tw-text-[10px] tw-text-content-faint">
              © {new Date().getFullYear()} Teve · Powered by Siman Tech
            </div>
          </div>
          <p className="tw-mt-2 tw-text-xs tw-text-content-faint">Copyright & “Powered by” stay as-is.</p>
        </Card>
      </div>

      {/* Domains */}
      <Card title="Domains">
        <form onSubmit={addDomain} className="tw-mb-4 tw-flex tw-flex-wrap tw-gap-2">
          <Input
            className="tw-max-w-sm"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder="portal.yourbrand.com"
          />
          <Button type="submit" icon="fa-plus" disabled={adding}>{adding ? 'Adding…' : 'Add domain'}</Button>
        </form>

        {domains.length === 0 ? (
          <EmptyState icon="fa-globe" title="No custom domains yet" hint="Add a subdomain you control, then point its DNS to app.teve.ca." />
        ) : (
          <div className="tw-divide-y tw-divide-line">
            {domains.map((d) => (
              <div key={d.id} className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-py-3">
                <div className="tw-mr-auto">
                  <div className="tw-font-semibold tw-text-heading">{d.domain}</div>
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-text-xs tw-text-content-faint">
                    <Badge tone={statusTone(d.status)}>{d.status}</Badge>
                    {(d.status === 'verified' || d.status === 'active') && (
                      <Badge tone={sslTone(d.ssl_status)}>{sslLabel(d.ssl_status)}</Badge>
                    )}
                    {d.verified_at && <span>verified {new Date(d.verified_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                {d.status !== 'verified' && d.status !== 'active' && (
                  <>
                    <Button variant="ghost" size="sm" icon="fa-circle-info" onClick={() => showInstructions(d)}>DNS setup</Button>
                    <Button variant="secondary" size="sm" icon="fa-check" onClick={() => verify(d)} disabled={verifyingId === d.id}>
                      {verifyingId === d.id ? 'Checking…' : 'Verify'}
                    </Button>
                  </>
                )}
                {(d.status === 'verified' || d.status === 'active') && d.ssl_status !== 'active' && (
                  <Button variant="secondary" size="sm" icon="fa-lock" onClick={() => retrySSL(d)} disabled={verifyingId === d.id}>
                    {verifyingId === d.id ? 'Issuing…' : 'Issue SSL'}
                  </Button>
                )}
                <Button variant="ghost" size="sm" icon="fa-trash" onClick={() => removeDomain(d)} />
              </div>
            ))}
          </div>
        )}

        <div className="tw-mt-4 tw-rounded-lg tw-border tw-border-info/30 tw-bg-info/10 tw-px-3 tw-py-2 tw-text-xs tw-text-info">
          <strong>HTTPS is automatic.</strong> As soon as a domain verifies, we issue a free Let's Encrypt certificate and enable SSL for it — usually within a minute. If it's still pending, make sure the domain isn't proxied (grey-cloud it on Cloudflare during setup) and click “Issue SSL”.
        </div>
      </Card>

      {/* DNS instructions modal */}
      {instructionsFor && (
        <>
          <div className="tw-fixed tw-inset-0 tw-z-40 tw-bg-black/50" onClick={() => setInstructionsFor(null)} />
          <div className="tw-fixed tw-left-1/2 tw-top-1/2 tw-z-50 tw-w-[90vw] tw-max-w-lg tw--translate-x-1/2 tw--translate-y-1/2 tw-rounded-2xl tw-border tw-border-line tw-bg-surface tw-p-6 tw-shadow-pop">
            <h3 className="tw-text-lg tw-font-bold tw-text-heading">Set up {instructionsFor.domain.domain}</h3>
            <p className="tw-mt-1 tw-text-sm tw-text-content-muted">{instructionsFor.data?.description}</p>

            {(() => {
              const rec = instructionsFor.data?.record || {
                type: 'CNAME', name: instructionsFor.domain.domain, value: 'app.teve.ca',
              };
              return (
                <div className="tw-mt-4 tw-rounded-lg tw-border tw-border-line tw-bg-sunken tw-p-3 tw-font-mono tw-text-xs">
                  <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-x-4 tw-gap-y-1">
                    <span className="tw-text-content-faint">Type</span><span className="tw-text-heading">{rec.type}</span>
                    <span className="tw-text-content-faint">Name / Host</span><span className="tw-break-all tw-text-heading">{rec.name}</span>
                    <span className="tw-text-content-faint">Value / Target</span><span className="tw-break-all tw-text-heading">{rec.value}</span>
                  </div>
                </div>
              );
            })()}

            <ol className="tw-mt-4 tw-space-y-1.5 tw-text-sm tw-text-content-muted tw-list-decimal tw-pl-5">
              {(instructionsFor.data?.steps || []).map((s, i) => <li key={i}>{s}</li>)}
            </ol>

            <div className="tw-mt-3 tw-rounded-lg tw-border tw-border-warn/30 tw-bg-warn/10 tw-px-3 tw-py-2 tw-text-xs tw-text-warn">
              A CNAME must be the only record on that name — don't also add a TXT or A record on {instructionsFor.domain.domain}. Verification uses the CNAME itself.
            </div>

            {instructionsFor.data?.verification_token && (
              <details className="tw-mt-3 tw-text-xs tw-text-content-faint">
                <summary className="tw-cursor-pointer">Can't point the domain yet? Verify with a TXT record instead</summary>
                <div className="tw-mt-2 tw-rounded-lg tw-bg-sunken tw-p-2 tw-font-mono tw-break-all">
                  TXT &nbsp;_teve-challenge.{instructionsFor.domain.domain} &nbsp;=&nbsp; teve-verify={instructionsFor.data.verification_token}
                </div>
              </details>
            )}
            <div className="tw-mt-5 tw-flex tw-justify-end tw-gap-2">
              <Button variant="secondary" onClick={() => setInstructionsFor(null)}>Close</Button>
              <Button icon="fa-check" onClick={() => verify(instructionsFor.domain)} disabled={verifyingId === instructionsFor.domain.id}>
                {verifyingId === instructionsFor.domain.id ? 'Checking…' : 'Verify now'}
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CustomDomainManager;
